import type { ReactNode } from "react";

import { num } from "../lib/format";
import { PERIODO_TIPICO_S, estadoMar } from "../lib/ondulacao";
import type { ImuPoint } from "../types";

interface Props {
  /** Histórico `imu` da boia do painel, no intervalo escolhido. */
  imu: ImuPoint[];
  /** O histórico bateu no teto de páginas — os números cobrem só o princípio. */
  truncado: boolean;
}

/**
 * O mar em palavras: estado, altura, swell e período — o que se diz a quem
 * pergunta "como está o mar?", em vez de pitch e roll em graus. Os números
 * técnicos continuam na API; aqui traduz-se.
 *
 * Só da boia do painel, como a autonomia: são grandezas operacionais de cada
 * nó, e o histórico `imu` só se pede para a primeira.
 */
export function EstadoMar({ imu, truncado }: Props): ReactNode {
  const mar = estadoMar(imu);

  return (
    <div className="seccao">
      <h3>Estado do mar</h3>
      {imu.length === 0 ? (
        // Ausência escrita por extenso, como em todo o lado: uma secção que
        // desaparecesse em silêncio lia-se como "não existe esta medição".
        <p className="nota" style={{ marginTop: 8, marginBottom: 0 }}>
          Sem blocos de movimento no intervalo.
        </p>
      ) : (
        <>
          <div className="metricas">
            <div className="metrica">
              <span className="rotulo">Estado</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {mar.rotulo === null ? "—" : `${mar.rotulo} (grau ${mar.grau})`}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Altura das ondas</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {mar.alturaM === null ? "—" : `${num(mar.alturaM, 2)} m`}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Ondulação de fundo (swell)</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {mar.swellM === null ? "—" : `${num(mar.swellM, 2)} m`}
              </div>
            </div>
            <div className="metrica">
              <span className="rotulo">Período das ondas</span>
              <div className="valor" style={{ fontSize: 15 }}>
                {mar.periodoS === null ? "—" : `${num(mar.periodoS, 1)} s`}
              </div>
            </div>
          </div>
          <p className="nota" style={{ marginTop: 8, marginBottom: 0 }}>
            {mar.fonte === "medida"
              ? "Calculado na boia a partir do movimento vertical (janelas de 60 s; " +
                "mostra-se a mediana das últimas). A altura é a significativa — a média " +
                "do terço mais alto das ondas; o swell é a parcela com período acima de 8 s."
              : `Boia sem estatística de ondas no firmware: altura estimada do movimento ` +
                `vertical assumindo o período típico de ${PERIODO_TIPICO_S} s da costa — o período ` +
                `e o swell ficam por medir até a boia atualizar.`}{" "}
            O estado segue a escala de Douglas, de 0 (espelhado) a 9 (excecional).
            {truncado &&
              " Intervalo grande: os números cobrem só o princípio da janela — encurta o intervalo."}
          </p>
        </>
      )}
    </div>
  );
}
